import React, { useCallback, useEffect, useState } from 'react';

import { apiService } from '../../components/Config/api.service';

const ORIGINES = {
    import: 'Import de fichier',
    bridge: 'Pont de paie',
};

const formatMontant = (valeur) =>
    new Intl.NumberFormat('fr-FR').format(Math.round(valeur || 0));

const formatPeriode = (periode) => {
    if (!periode) {
        return '';
    }
    const [annee, mois] = periode.split('-');
    return `${mois}/${annee}`;
};

const PayrollLines = () => {
    const [lignes, setLignes] = useState([]);
    const [mois, setMois] = useState('');
    const [recherche, setRecherche] = useState('');
    const [enCours, setEnCours] = useState(true);
    const [erreur, setErreur] = useState(null);

    const charger = useCallback(async (periode) => {
        setEnCours(true);
        setErreur(null);
        try {
            // Le mois n'est envoyé qu'une fois complet : le champ month émet
            // aussi des valeurs partielles pendant la saisie.
            const filtre = /^\d{4}-\d{2}$/.test(periode) ? periode : undefined;
            setLignes(await apiService.getPayrollLines(filtre));
        } catch (error) {
            setErreur(error?.userMessage || "Les lignes de paie n'ont pas pu être chargées.");
            setLignes([]);
        } finally {
            setEnCours(false);
        }
    }, []);

    useEffect(() => {
        charger(mois);
    }, [charger, mois]);

    // Filtre côté client : sans pagination, tout le mois est déjà chargé, donc
    // pas de requête ni d'anti-rebond. À déplacer côté serveur le jour où la
    // liste sera paginée.
    const terme = recherche.trim().toLowerCase();
    const lignesVisibles = terme
        ? lignes.filter(
            (ligne) =>
                (ligne.employee_id?.[1] || '').toLowerCase().includes(terme) ||
                (ligne.matricule || '').toLowerCase().includes(terme)
        )
        : lignes;

    return (
        <div className="p-4">
            <h1 className="text-2xl font-semibold mb-2">Lignes de paie</h1>
            <p className="mb-6 text-sm opacity-80">
                Les éléments de paie enregistrés pour votre société, qu'ils viennent d'un import de
                fichier ou du pont de paie.
            </p>

            <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex flex-col gap-1 max-w-xs">
                    <label htmlFor="payroll-lines-month" className="text-sm font-medium">
                        Mois
                    </label>
                    <input
                        id="payroll-lines-month"
                        type="month"
                        value={mois}
                        onChange={(event) => setMois(event.target.value)}
                        className="input input-bordered"
                    />
                </div>

                <div className="flex flex-col gap-1 max-w-xs">
                    <label htmlFor="payroll-lines-search" className="text-sm font-medium">
                        Nom ou matricule
                    </label>
                    <input
                        id="payroll-lines-search"
                        type="search"
                        value={recherche}
                        onChange={(event) => setRecherche(event.target.value)}
                        placeholder="Rechercher un employé"
                        className="input input-bordered"
                    />
                </div>
            </div>

            {erreur && (
                <div role="alert" className="alert alert-error mb-4">
                    {erreur}
                </div>
            )}

            {!erreur && enCours && <p>Chargement…</p>}

            {!erreur && !enCours && lignes.length === 0 && (
                <p className="opacity-80">
                    Aucune ligne de paie pour cette période. Importez un fichier depuis la section
                    « Import de paie », ou changez de mois.
                </p>
            )}

            {!erreur && !enCours && lignes.length > 0 && lignesVisibles.length === 0 && (
                <p className="opacity-80">
                    Aucune ligne ne correspond à « {recherche} » sur cette période.
                </p>
            )}

            {!erreur && !enCours && lignesVisibles.length > 0 && (
                <table className="table w-full">
                    <thead>
                        <tr>
                            <th scope="col">Matricule</th>
                            <th scope="col">Employé</th>
                            <th scope="col">Mois</th>
                            <th scope="col">Brut</th>
                            <th scope="col">Net</th>
                            <th scope="col">Heures sup.</th>
                            <th scope="col">Primes</th>
                            <th scope="col">Origine</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lignesVisibles.map((ligne) => (
                            <tr key={ligne.id}>
                                <td>{ligne.matricule || '—'}</td>
                                <td>{ligne.employee_id?.[1] || '—'}</td>
                                <td>{formatPeriode(ligne.period)}</td>
                                <td>{formatMontant(ligne.gross)}</td>
                                <td>{formatMontant(ligne.net)}</td>
                                <td>{ligne.overtime_hours || 0}</td>
                                <td>{formatMontant(ligne.bonuses)}</td>
                                <td>{ORIGINES[ligne.source] || ligne.source}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default PayrollLines;
